import { useState } from "react";
import { Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { PanelField } from "../panelFields";
import { AsyncButton } from "../../../common/buttons";
import { FIELD_MONO_STYLE } from "../wizardStyles";
import { generateDDNS, verifyDDNS } from "../../../../services/installationService";
import { captureWizardError } from "../shared/wizardObservability";

function describeError(err) {
  if (err?.code === "ECONNABORTED") return "Check timed out";
  if (!err?.response) return "Network error";
  if (err.response.status >= 500) return "Server error, please retry";
  return err.response.data?.detail ?? "Check failed";
}

function VerifyResult({ result }) {
  if (!result) return null;
  if (result.kind === "error") {
    return (
      <small className="text-danger">
        <i className="fas fa-times-circle mr-1" />
        {result.message}
      </small>
    );
  }
  const { success, resolved_ip: resolvedIp, detail } = result.data;
  const color = success ? "text-success" : "text-danger";
  const icon = success ? "fa-check-circle" : "fa-times-circle";
  const message = success ? `Resolved to ${resolvedIp}` : detail;
  return (
    <small className={color}>
      <i className={`fas ${icon} mr-1`} />
      {message}
    </small>
  );
}

/**
 * @param {import("../WizardCard").StepPanelProps} props
 *
 * Editable DDNS fields, with buttons to generate and check the record.
 */
export default function DdnsPanel({ installation, setField, mergeServerFields, status, bottomNav }) {
  const hasAllCredentials = Boolean(
    installation?.ddns_url && installation?.ddns_username && installation?.ddns_password
  );
  const identifier = installation?.uid ?? installation?.deal_id;
  const canCheck = Boolean(identifier && installation?.ddns_url);
  const canGenerate = Boolean(identifier && !installation?.ddns_url);
  const [result, setResult] = useState(null);
  const [generateError, setGenerateError] = useState(null);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerateError(null);
    try {
      const { data } = await generateDDNS(identifier);
      mergeServerFields?.(data);
    } catch (err) {
      captureWizardError(err, "generateDDNS", { identifier });
      setGenerateError(describeError(err));
    }
  };

  const handleCheck = async () => {
    if (!canCheck) return;
    setResult(null);
    try {
      const { data } = await verifyDDNS(identifier);
      setResult({ kind: "ok", data });
    } catch (err) {
      captureWizardError(err, "verifyDDNS", { identifier });
      setResult({ kind: "error", message: describeError(err) });
    }
  };

  return (
    <WizardPanel
      title="DDNS Configuration"
      subtitle="DDNS credentials used for remote access to the store"
      status={status}
      bottomNav={bottomNav}
    >
      {!hasAllCredentials && (
        <small className="text-muted d-block mb-2">
          <i className="fas fa-info-circle mr-1" />
          Generate the record automatically, or enter the URL and credentials manually.
        </small>
      )}

      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-0">
                <Label className="text-muted small mb-1">DDNS URL</Label>
                <Input
                  value={installation?.ddns_url ?? ""}
                  placeholder="e.g. fr-store-1234.link-dvr.com"
                  autoComplete="off"
                  onChange={(e) => setField("ddns_url", e.target.value)}
                  style={FIELD_MONO_STYLE}
                />
                <small className="text-muted d-block mt-1">
                  <i className="fas fa-info-circle mr-1" />
                  Enter the full hostname (e.g. <code>my-store.dyndns.org</code>).
                </small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <PanelField
                label="DDNS username"
                value={installation?.ddns_username}
                onChange={(v) => setField("ddns_username", v)}
                autoComplete="off"
              />
            </Col>
            <Col md={6}>
              <PanelField
                label="DDNS password"
                value={installation?.ddns_password}
                onChange={(v) => setField("ddns_password", v)}
                autoComplete="off"
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
        {canGenerate && (
          <AsyncButton color="primary" onClick={handleGenerate}>
            <i className="fas fa-bolt mr-2" />
            Generate DDNS
          </AsyncButton>
        )}
        <AsyncButton color="primary" outline disabled={!canCheck} onClick={handleCheck}>
          <i className="fas fa-play mr-2" />
          Check DDNS
        </AsyncButton>
        {canCheck && <VerifyResult result={result} />}
        {generateError && (
          <small className="text-danger">
            <i className="fas fa-times-circle mr-1" />
            {generateError}
          </small>
        )}
      </div>
    </WizardPanel>
  );
}
